import React from 'react';
import ReactDOM from 'react-dom';
import { SeatingTabSeat } from 'Services/Api/_HumanWritten/SeatingService';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { buildSeatGrid, columnSides, SIDE_LABELS } from 'Modules/Ferries/Utils/seatGrid';
import { DeckFeature, dividerAt, featureCovers, isTableCell, markerAt } from 'Modules/Ferries/Utils/deckFeatures';
import { shortSeatLabel } from './seatingLayout';
import SeatInfoPanel, { SeatAnchor, SeatInfo } from './SeatInfoPanel';

/**
 * The space between one side of the vessel and the next.
 *
 * <p>Kept for anything that has to measure the plan in JavaScript. What the plan itself draws comes
 * from `--seat-side-gap`, so the gap shrinks with the seats on a phone.</p>
 */
export const SIDE_GAP = 24;

/** How one seat is drawn, decided by whoever is reading the plan. */
export interface SeatAppearance {
	background: string;
	/** Tailwind class for the seat number, because the readable colour follows the fill. */
	textClass: string;
	/** What the seat is, said after its label in the tooltip — "already taken", "available". */
	title: string;
	/** Whether clicking it does anything. */
	selectable?: boolean;
	/** Drawn as an outline rather than a fill: something on the plan that is not a seat. */
	outlined?: boolean;
	/** A border in the status's own darker shade, where the reader is meant to see the seat's edge. */
	border?: string;
	/**
	 * Faded, because the reader has asked to see a different status.
	 *
	 * <p>Decided by the caller rather than here: the grid draws a plan and does not know what has
	 * been filtered for. Fading rather than hiding, so the shape of the deck survives the filter —
	 * "where are the four still to board" is a question about where they are ON the vessel.</p>
	 */
	dimmed?: boolean;
	/**
	 * The seat's panel, laid out — who, fare type, whether aboard.
	 *
	 * <p>Optional: a reader with only a word to say ("already taken") leaves it out and the panel
	 * shows `title` as a sentence.</p>
	 */
	info?: SeatInfo;
}

interface Props {
	seats: SeatingTabSeat[];
	features: DeckFeature[];
	/**
	 * How big to draw a seat.
	 *
	 * <p><c>regular</c> is the booking wizard's: a 40px seat a thumb can hit while choosing one.
	 * <c>plan</c> is the check-in boarding overview's, which is read rather than tapped and shows a
	 * whole vessel at once — so its seats are smaller, and smaller again on a phone. The sizes live
	 * in SCSS rather than here because only the stylesheet knows where the phone breakpoint is.</p>
	 */
	size?: 'regular' | 'plan';
	/**
	 * How to draw each seat.
	 *
	 * <p>The one thing that differs between the two readers of this plan. A booker is asking "can I
	 * sit here", so their seats are green, grey and blue; a deckhand at the gate is asking "who is
	 * aboard", so theirs are free, booked and embarked. The geometry — the blocks, the sides, the
	 * gangways, the tables, which row lines up with which — is the vessel's and is the same for
	 * both, which is why it lives here and the colours do not.</p>
	 */
	appearance: (seat: SeatingTabSeat) => SeatAppearance;
	onSelect?: (seat: SeatingTabSeat) => void;
}

/**
 * The deck, laid out as the vessel is laid out.
 *
 * <p>One grid for the whole deck, with the sides separated by a gap inside it rather than by three
 * boxes side by side. Three boxes each sorted and sized themselves, so row 6 in the port block did
 * not line up with row 6 in the centre — and a deck plan is read across, which is the one thing
 * that made impossible.</p>
 */
export default function SeatingDeckGrid({ seats, features, appearance, onSelect, size = 'regular' }: Props) {
	const grid = React.useMemo(() => buildSeatGrid(seats), [seats]);

	/*
		What a seat says about itself, on a tap.

		It was the button's `title`, which is a hover tooltip — so on a phone, which is most of the
		gate, there was no way to ask at all, and on a desktop it took a second of hovering to find
		out who is in 5G. One at a time by construction: this holds a single seat, so opening one
		closes whichever was open.

		Pinned with viewport coordinates and portalled to the body, for the same reason
		`ClassInfoPopover` is: the plan sits in a box that scrolls sideways, and a panel drawn inside
		that box is clipped at its edge.
	*/
	const [open, setOpen] = React.useState<{
		id: string;
		label: string;
		text: string;
		info?: SeatInfo;
		anchor: SeatAnchor;
	} | null>(null);

	const gridRef = React.useRef<HTMLDivElement>(null);

	const showInfo = (seat: SeatingTabSeat, text: string, info: SeatInfo | undefined, element: HTMLElement) => {
		setOpen(current => {
			if (current?.id === seat.id) {
				return null;
			}
			const rect = element.getBoundingClientRect();
			return {
				id: seat.id,
				label: seat.label,
				text,
				info,
				anchor: { top: rect.top, bottom: rect.bottom, centre: rect.left + rect.width / 2 },
			};
		});
	};

	// Anything that moves the plan under the panel closes it rather than leaving it pointing at a
	// seat that is no longer there — it is pinned to a viewport position, not to the seat.
	React.useEffect(() => {
		if (!open) {
			return undefined;
		}
		const close = () => setOpen(null);
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				close();
			}
		};
		/*
			A press anywhere else puts it away.

			Seats are exempt, and have to be: this runs on `mousedown`, before the seat's own click,
			so closing on a press that lands on a seat would shut the panel a moment before that
			seat's click asked to toggle it — and tapping the open seat would reopen it instead of
			closing it. A press on another seat is left to that seat, which swaps the panel over.
		*/
		const onPressElsewhere = (event: MouseEvent) => {
			if ((event.target as HTMLElement | null)?.closest('[data-seat]')) {
				return;
			}
			close();
		};
		document.addEventListener('keydown', onKeyDown);
		document.addEventListener('mousedown', onPressElsewhere);
		window.addEventListener('resize', close);
		// Capturing, so a scroll of the plan's own box reaches this and not just a scroll of the page.
		window.addEventListener('scroll', close, true);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('mousedown', onPressElsewhere);
			window.removeEventListener('resize', close);
			window.removeEventListener('scroll', close, true);
		};
	}, [open]);

	// Where the vessel changes sides, so the gap falls between port and centre rather than between
	// two arbitrary seat numbers.
	const sides = React.useMemo(() => columnSides(seats, grid.columns), [seats, grid.columns]);
	const sideGapAfter = (index: number) => !!sides[index] && !!sides[index + 1] && sides[index] !== sides[index + 1];

	/** Each run of columns that share a side, for the band of labels above the grid. */
	const sideBands = React.useMemo(() => {
		const bands: { side: string | null; span: number }[] = [];
		grid.columns.forEach((_, index) => {
			const side = sides[index] ?? null;
			const last = bands[bands.length - 1];
			if (last && last.side === side) {
				last.span += 1;
				return;
			}
			bands.push({ side, span: 1 });
		});
		return bands;
	}, [grid.columns, sides]);

	return (
		<div ref={gridRef} className={`seat-grid seat-grid--${size} inline-flex flex-col`}>
			{sideBands.some(band => band.side) && (
				<div className="seat-grid__row">
					{sideBands.map((band, index) => (
						<React.Fragment key={`${band.side ?? 'none'}-${index}`}>
							{/*
								As wide as the seats beneath it, to the pixel — the width is computed
								in CSS from the same `--seat-w` and `--seat-gap` the rows use, so a
								band cannot drift out of line when the seat size changes.
							*/}
							<div
								className="seat-grid__band-label"
								style={{ '--span': band.span } as React.CSSProperties}
							>
								{band.side ? (SIDE_LABELS[band.side] ?? band.side) : ''}
							</div>
							{index + 1 < sideBands.length && <div className="seat-grid__side-gap" />}
						</React.Fragment>
					))}
				</div>
			)}

			{grid.rows.map(row => (
				<div key={row.rowLabel} className="seat-grid__row">
					{row.cells.map((seat, index) => {
						const seatNumber = grid.columns[index];
						// Same resolution the admin layout uses, so the reader sees the deck the admin drew.
						const found = dividerAt(features, seatNumber, 'col');
						const divider =
							found && featureCovers(found, row.rowLabel)
								? {
										size: found.type.startsWith('table') ? 14 : 10,
										colour: found.type.startsWith('table') ? '#fde68a' : '#d1d5db',
										title: found.type.startsWith('table') ? 'Table' : 'Aisle',
									}
								: null;
						const gangway = (
							<>
								{divider && (
									<div
										title={divider.title}
										className="shrink-0 rounded"
										style={{ width: divider.size, background: divider.colour }}
									/>
								)}
								{sideGapAfter(index) && <div className="seat-grid__side-gap" />}
							</>
						);
						if (!seat) {
							const table = isTableCell(features, row.rowLabel, seatNumber);
							const marker = markerAt(features, row.rowLabel, seatNumber);
							return (
								<React.Fragment key={seatNumber}>
									<div
										title={marker?.label ?? marker?.icon}
										className={`seat-grid__cell shrink-0 rounded flex items-center justify-center ${
											table ? 'border border-yellow-300' : ''
										}`}
										style={{
											background: table ? '#fde68a' : undefined,
											color: marker?.colour ?? '#4b5563',
										}}
									>
										{marker?.icon && (
											<Icon
												name={marker.icon}
												useMaterialIcons
												classname="text-sm leading-none"
											/>
										)}
									</div>
									{gangway}
								</React.Fragment>
							);
						}

						const drawn = appearance(seat);
						const clickable = !!onSelect && !!drawn.selectable;

						// Square at the back, rounded at the front, the way the printed plan draws it.
						// The deck is always shown bow-up here, so forward is up.
						const borderRadius = (() => {
							if (!seat.facing) {
								return '7px';
							}
							return seat.facing === 'FORWARD' ? '12px 12px 3px 3px' : '3px 3px 12px 12px';
						})();

						const label = `${seat.label} — ${drawn.title}`;
						// A seat that cannot be chosen can still be asked about. Where it CAN be
						// chosen the tap belongs to choosing: that is what the reader came to do, and
						// an info panel in front of it would be one tap between them and their seat.
						const asksInfo = !clickable && !!drawn.title;
						const isOpen = open?.id === seat.id;

						return (
							<React.Fragment key={seat.id}>
								<button
									type="button"
									data-seat
									aria-label={label}
									aria-expanded={asksInfo ? isOpen : undefined}
									disabled={!clickable && !asksInfo}
									onClick={event => {
										if (clickable) {
											onSelect?.(seat);
											return;
										}
										showInfo(seat, drawn.title, drawn.info, event.currentTarget);
									}}
									className={`seat-grid__seat shrink-0 leading-none ${drawn.textClass} ${
										clickable || asksInfo ? 'cursor-pointer' : 'cursor-not-allowed'
									} ${drawn.outlined ? 'border border-gray-300' : ''} ${
										isOpen ? 'seat-grid__seat--open' : ''
									} ${drawn.dimmed ? 'seat-grid__seat--dimmed' : ''}`}
									style={{
										background: drawn.background,
										borderRadius,
										border: drawn.border ? `1px solid ${drawn.border}` : undefined,
									}}
								>
									{seat.kind === 'WHEELCHAIR_BAY' ? (
										<Icon name="accessible" useMaterialIcons classname="text-sm leading-none" />
									) : (
										// Without the deck's code: the deck is the tab being looked at, so
										// repeating it on all two hundred seats only costs width.
										shortSeatLabel(seat)
									)}
								</button>
								{gangway}
							</React.Fragment>
						);
					})}
				</div>
			))}

			{open &&
				ReactDOM.createPortal(
					<SeatInfoPanel
						seatLabel={open.label}
						anchor={open.anchor}
						info={open.info}
						fallback={open.text}
						onClose={() => setOpen(null)}
					/>,
					/*
						The app's whole stylesheet is nested inside `.frontend` (see scss/main.scss), so
						every rule ships as `.frontend .seat-info`, never `.seat-info`. A body-level
						portal therefore rendered this panel OUTSIDE the only scope its styles exist
						in: right text, right place, and no styling at all — static, transparent,
						black-on-white and 21px tall in the top-left corner, which reads as "tapping a
						seat does nothing".

						The app ROOT rather than the grid's nearest such ancestor, which is what
						`Popover` uses and what this used at first. The two differ here: this plan is
						drawn inside a modal, and react-modal's own portal wrapper carries
						`portal frontend` (NewModal passes `store.appLocation`), so climbing to the
						NEAREST match lands inside the modal — a stacking context the panel then cannot
						paint above, however high its z-index. Measured: `elementFromPoint` at the
						panel's own centre returned the modal, not the panel. Taking the first
						`.frontend` in the document instead puts it beside the modal, where its
						z-index: 10000 means what it says.

						`position: fixed` survives either way, because nothing between here and the
						root carries a transform, filter or containment.
					*/
					document.querySelector('.frontend, .admin') ?? document.body,
				)}
		</div>
	);
}
